import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../../types/store';
import { DrawerItem } from './DrawerItem';
import { logout } from '../../store/Reducers/UserSlice';
import { findActivetitle } from '../../helpers';
import { CHANGE_PASSWORD_ROUTE } from '../../constants/routes';
import { IDrawerProps } from '../../types/drawer';

const drawerWidth = 240;

export default function ResponsiveDrawer(props: Readonly<IDrawerProps>) {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const menuItems = useSelector((state: IRootState) => state.user.menuItems);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const drawer = (
    <div>
      <Toolbar
        sx={{
          minHeight: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#000',
        }}
      >
        <Box
          component="img"
          src="https://superstore.com.fj/wp-content/uploads/2024/08/logo.svg"
          alt="Superstore"
          sx={{
            height: 48, // control logo size
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      </Toolbar>

      <Divider />
      <List>
        {menuItems.map((item) => (
          <DrawerItem key={item.title} item={item} />
        ))}
      </List>
      <Divider />
      <List>
        <ListItem key={'Change Password'} disablePadding>
          <ListItemButton
            onClick={() => {
              navigate(CHANGE_PASSWORD_ROUTE);
            }}
          >
            <ListItemText
              primary={'Change Password'}
              slotProps={{
                primary: {
                  margin: 0,
                  fontSize: 14,
                },
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem key={'Logout'} disablePadding>
          <ListItemButton
            onClick={() => {
              dispatch(logout());
              localStorage.clear();
            }}
          >
            <ListItemText
              primary={'Logout'}
              slotProps={{
                primary: {
                  margin: 0,
                  fontSize: 14,
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  // Remove this const when copying and pasting into your project.
  const container = window === undefined ? undefined : () => window().document.body;
  const location = useLocation();

  const activetitle = findActivetitle(menuItems, location.pathname);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar
          sx={{
            backgroundColor: '#000',
          }}
        >
          <IconButton
            color="inherit"
            aria-title="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {activetitle}
          </Typography>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-title="mailbox folders"
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          slotProps={{
            root: {
              keepMounted: true, // Better open performance on mobile.
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        <Box>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
